<script setup>
import { getHeaderHeight } from '#common/components/Header/utils'
import ga4 from '#common/utils/ga4'
import { baseURL } from '#common/utils/url'
import { identifyUser, pushPageView } from '#common/utils/user'

const metadata = useMetadata()
const { $auth, $axios, $store, $config, $cookies, hook } = useNuxtApp()
const $route = useRoute()
const unstuck = ref(false)
const headerChangeHeight = ref(0)
const lastScrollPosition = ref(0)

const {
  isLoading,
  showHeader,
  showFooter,
  showTermsAndConditions,
  showPPTOToast,
} = storeToRefs(useLayout())

const mobileSearchOpen = computed(() => $store.getters.mobileSearchOpen)
const toggleCart = computed(() => $store.getters.toggleCart)
const inMobileApp = computed(() => $store.getters.inMobileApp)
const mobileNotificationsOpen = computed(
  () => $store.getters.mobileNotificationsOpen,
)

const seoData = computed(() => {
  const data = metadata.seoForPath($route.path)
  if (!data) {
    return {
      page_title:
        // keep this at 60 characters or less!
        'Buy Limited Production Wines Online | The Wine Access Store',
      // This would be used for a <h1> in <head> but that is not supported
      h1_tag: '',
      description: process.description, // pull from head.js
    }
  }
  return data
})

function adjustAppContentSize() {
  if (import.meta.client) {
    // Wait for child Header component finish rendering before checking height and adjusting
    nextTick(() => {
      const height = getHeaderHeight()
      headerChangeHeight.value = window.innerHeight - height * 2
      unstickHeaderOnScroll()
    })
  }
}
function pathToPushName(name, to) {
  const wagtailTitle = document.getElementById('wagtail-page-title')
  if (name === 'club-gifting-slug') {
    return `${to.params.slug}-gifting`
  }
  if (name === 'store-category-slug' && to.params.category === 'occasion') {
    return to.params.slug
  }
  if (wagtailTitle) {
    return wagtailTitle.textContent
  }
  const map = {
    index: 'Home',
    'catalog-slug': document.title,
    'store-category-slug': 'Product Listing Page',
  }
  if (name in map) {
    return map[name]
  }
  return $route.name
}
function unstickHeaderOnScroll() {
  if (import.meta.client) {
    // Wait for child Header component finish rendering before checking scroll position
    nextTick(() => {
      const scroll = window.pageYOffset
      unstuck.value =
        lastScrollPosition.value > headerChangeHeight.value &&
        lastScrollPosition.value < scroll
      lastScrollPosition.value = scroll
    })
  }
}
function link() {
  const theBaseURL = $config.public.baseUrl
  const currentPath = $route.fullPath
  const url = new URL(theBaseURL + currentPath)
  const pageValue = url.searchParams.get('page')

  url.search = ''
  if (pageValue) url.searchParams.set('page', pageValue)

  return [
    {
      // Use 'hid' to prevent re-adding a new tag on every route change
      hid: 'canonical',
      rel: 'canonical',
      href: url.toString(),
    },
  ]
}
function meta() {
  const ogImageItems = seoData.value.og_image_url
    ? [
        {
          hid: 'og:image',
          name: 'og:image',
          property: 'og:image',
          content: seoData.value.og_image_url,
        },
        {
          hid: 'og:image:width',
          name: 'og:image:width',
          property: 'og:image:width',
          content: seoData.value.og_image_width,
        },
        {
          hid: 'og:image:height',
          name: 'og:image:height',
          property: 'og:image:height',
          content: seoData.value.og_image_height,
        },
        {
          hid: 'og:image:type',
          name: 'og:image:type',
          property: 'og:image:type',
          content: seoData.value.og_image_type,
        },
      ]
    : []
  return [
    {
      hid: 'created',
      name: 'created',
      content: moment(Date.now()).format('Do MMM YYYY h:mm'),
    },
    {
      hid: 'og:url',
      name: 'og:url',
      property: 'og:url',
      content: $route.fullPath,
    },
    {
      hid: 'description',
      name: 'description',
      content: seoData.value.description,
    },
    {
      hid: 'og:description',
      name: 'og:description',
      content: seoData.value.description,
    },
    {
      hid: 'og:title',
      name: 'og:title',
      content: seoData.value.page_title,
    },
    {
      hid: 'format-detection',
      name: 'format-detection',
      property: 'format-detection',
      content: 'telephone=no',
    },
    {
      hid: 'apple-itunes-app',
      name: 'apple-itunes-app',
      content: `app-id=1642804318, app-argument=${baseURL() + $route.fullPath}`,
    },
    ...ogImageItems,
  ]
}

watch($route, (to, from) => {
  // internally, referrer is the page that we are coming from during
  // a page transition.
  if (import.meta.client) {
    const queryParams = new URLSearchParams(from.params).toString()
    const referrerPath =
      window.location.protocol.concat('//').concat(window.location.host) +
      from.path
    const referrerUrl = queryParams
      ? referrerPath + '?' + queryParams
      : referrerPath

    // Delay this call to try and guarantee that the document
    // title has been updated at the point of sending the pageView
    setTimeout(() => {
      pushPageView($axios, {
        name: pathToPushName(to.name, to),
        properties: {
          referrer: referrerUrl,
          path: to.fullPath,
          url: window.location.href,
          title: document.title,
        },
      })
    }, 3000)
  }
})
watch(
  () => showHeader.value,
  (newValue) => {
    if (newValue) {
      adjustAppContentSize()
    }
  },
)
onMounted(async () => {
  adjustAppContentSize()
  // Listen for resize events
  window.addEventListener('resize', adjustAppContentSize)
  window.addEventListener('scroll', unstickHeaderOnScroll)
  if ($auth.loggedIn) {
    ga4.config({ user: $auth.user })
    identifyUser($auth, $cookies)
  } else {
    ga4.config()
  }
  if (!$config.public.isProductionBuild) {
    // Add ?force_error=true to the end of a url to force an unhandled 500 error in axios
    if ($route.query.force_error) {
      await $axios.get('/api/500error/')
    }
  }
  pushPageView($axios, {
    name: pathToPushName($route.name, $route),
    properties: {
      path: $route.fullPath,
      url: window.location.href,
      title: document.title,
      referrer: document.referrer,
    },
  })
})
onBeforeUnmount(() => {
  window.removeEventListener('resize', adjustAppContentSize)
  window.removeEventListener('scroll', unstickHeaderOnScroll)
})

useHead({
  title: seoData.value.page_title,
  link: link(),
  meta: meta(),
  bodyAttrs: {
    class: toggleCart.value ? 'no-overflow' : '',
  },
  __dangerouslyDisableSanitizers: ['script'],
})
hook('page:loading:start', () => {
  isLoading.value = true
})
hook('page:loading:end', () => {
  // Some pages are responsible for their own async data fetching and will set the isLoading on their own
  // If you notice a page is loading for too short a time and data is still fetching, add the path here
  //    and manage the loading value on the page.
  const pathsResponsibleForLoading = [
    '/store/',
    '/checkout/',
    '/basket/',
    '/cart/',
    '/cellar/',
    '/accounts/orders/',
    '/en-primeur/',
  ]
  if (
    pathsResponsibleForLoading.some((pattern) => $route.path.includes(pattern))
  ) {
    return
  }
  isLoading.value = false
})
</script>

<template>
  <div>
    <div class="app-container">
      <ExitIntent />
      <PopupPromo />
      <div
        id="site-container"
        :class="{
          'site-container': true,
          unstuck: unstuck && showHeader,
        }"
      >
        <SiteHeader v-show="showHeader && !inMobileApp" />
        <main id="main">
          <Loading :initial="isLoading" />
          <NuxtPage :keepalive="true" />
        </main>
        <SiteFooter v-show="showFooter && !inMobileApp" />
        <div v-if="mobileNotificationsOpen" class="modal in-bottom slide-up">
          <AlertNotifications :mobile="true" />
        </div>
        <div
          v-show="mobileSearchOpen"
          class="search-modal modal in-bottom slide-up"
        >
          <Search />
        </div>
      </div>
    </div>
    <SideCart />
    <client-only>
      <LogInModal
        :visible="$auth.authModalOpen"
        @close="$auth.closeAuthModal()"
      />
    </client-only>
    <PPTOToastification v-if="!inMobileApp && showPPTOToast" />
    <UserListToast />
    <TermsAndConditionsPopup
      :visible="showTermsAndConditions"
      @close="showTermsAndConditions.value = false"
    />
  </div>
</template>
